// Groq Cloud API Configuration
import { projectId, publicAnonKey } from './supabase/info';

const SERVER_URL = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

export const setGroqAPIKey = (apiKey: string) => {
  // This function is kept for compatibility but not used
  // API key is now managed by the backend server
};

export const getGroqAPIKey = (): string => {
  // API key is managed by the backend server
  return '';
};

export const clearGroqAPIKey = () => {
  // This function is kept for compatibility but not used
  // API key is now managed by the backend server
};

interface GroqMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface GroqResponse {
  choices?: Array<{
    message: {
      content: string;
    };
  }>;
  error?: {
    message: string;
  };
}

export const queryLlama = async (
  prompt: string,
  context?: string,
  retryCount = 0,
  maxRetries = 2
): Promise<string> => {
  try {
    const response = await fetch(`${SERVER_URL}/ai/query`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        context
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Invalid API key. Please check your Groq API key in Profile Settings.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait a moment before trying again.');
      } else if (response.status === 503 || response.status === 529) {
        // Service unavailable or over capacity
        const errorMessage = errorData.error || 'AI service is currently over capacity.';
        
        // Check if we should retry with exponential backoff
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
          return queryLlama(prompt, context, retryCount + 1, maxRetries);
        } else {
          // Max retries reached, fall back silently
          console.log('AI service over capacity - using fallback responses');
          throw new Error('AI_OVER_CAPACITY');
        }
      } else {
        const errorMessage = errorData.error || `API error: ${response.status}. Please try again.`;
        throw new Error(errorMessage);
      }
    }

    const data = await response.json();
    
    // Handle the response
    if (data.response) {
      return data.response;
    } else if (data.error) {
      // Check for over capacity errors in the response
      if (data.error.includes('over capacity') || data.error.includes('currently unavailable')) {
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return queryLlama(prompt, context, retryCount + 1, maxRetries);
        } else {
          console.log('AI service over capacity - using fallback responses');
          throw new Error('AI_OVER_CAPACITY');
        }
      }
      throw new Error(data.error);
    } else {
      throw new Error('Unexpected response format from the API');
    }
  } catch (error: any) {
    // Handle network errors (CORS, no internet, etc.) silently
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      // Return a special error code that the UI can detect for silent fallback
      throw new Error('CORS_FALLBACK_SILENT');
    }
    
    // Don't log expected fallback errors
    if (error.message === 'AI_OVER_CAPACITY') {
      throw error;
    }
    
    // For other errors, provide helpful messages
    console.error('Groq API Error:', error);
    throw error;
  }
};

// Function to check if API key is configured
export const isGroqConfigured = (): boolean => {
  // API key is always configured on the backend
  return true;
};

// Function to get queue-specific insights
export const getQueueInsights = async (queueData: any): Promise<string> => {
  const context = `Current queue statistics:
- Total queues: ${queueData.totalQueues || 0}
- Waiting: ${queueData.waiting || 0}
- Serving: ${queueData.serving || 0}
- Completed: ${queueData.completed || 0}`;

  const prompt = 'Based on the current queue statistics, provide a brief insight or recommendation for improving queue management.';
  
  return queryLlama(prompt, context);
};

// Function to get service recommendations
export const getServiceRecommendations = async (userHistory: string[]): Promise<string> => {
  const context = `User's previous services: ${userHistory.join(', ')}`;
  const prompt = 'Based on the user\'s service history, suggest which services they might need next and explain why.';
  
  return queryLlama(prompt, context);
};